import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { campaignsSelectors } from 'store/selectors/campaigns';
import { deleteTargetGroup, fetchTargetGroups } from 'store/actions/campaigns';
import { setSnackbarSuccess } from 'store/actions/global';

import { useConfirm } from 'material-ui-confirm';
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Grid from 'material/components/Grid';
import { Add as AddIcon } from '@mui/icons-material';

import { colors } from 'themes';

import { Backdrop, BreadcrumbsNavigation, Container, LinkText } from 'common/components';
import TopCard from 'common/containers/TopCard';
import { CardWrapper, CircularProgressWrapper } from 'globalStyles';

import TargetGroupItem from '../components/TargetGroupItem';

const TargetGroupsList = () => {
  const dispatch = useDispatch();

  const { organizationUuid } = useParams();

  const { isDataFetching, targetGroups } = useSelector(state => ({
    isDataFetching: campaignsSelectors.isDataFetching(state),
    targetGroups: campaignsSelectors.getTargetGroups(state),
  }));

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/campaigns`,
      name: 'Advocacy',
    },
    {
      link: '',
      name: 'Target Groups',
    },
  ];

  useEffect(() => {
    dispatch(fetchTargetGroups({ organization: organizationUuid }));
  }, []);

  const confirm = useConfirm();

  const handleRemoveTargetGroup = targetGroupUuid => {
    confirm({
      confirmationText: 'Remove',
      title: 'Remove target group?',
    })
      .then(() => dispatch(deleteTargetGroup(targetGroupUuid)))
      .then(() => dispatch(fetchTargetGroups({ organization: organizationUuid })))
      .then(() => dispatch(setSnackbarSuccess()));
  };

  const headerCellSx = {
    color: colors.osloGray,
    fontSize: '14px',
    fontWeight: 500,
  };

  return (
    <>
      <BreadcrumbsNavigation links={breadcrumbs} />
      <Container>
        <Grid container>
          <TopCard
            title="Target Groups"
            actionButtons={[
              {
                color: 'secondary',
                component: RouterLink,
                disableElevation: true,
                name: 'New Target Group',
                startIcon: <AddIcon />,
                to: `/${organizationUuid}/create-target-group`,
                variant: 'contained',
              },
            ]}
          />
        </Grid>
        <CardWrapper>
          {isDataFetching ? (
            <CircularProgressWrapper>
              <CircularProgress />
            </CircularProgressWrapper>
          ) : (
            <>
              {!targetGroups?.length && (
                <LinkText color="inherit" to={`/${organizationUuid}/create-target-group`}>
                  <span>Create your first Target Group</span>
                </LinkText>
              )}
              {!!targetGroups?.length && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {targetGroups.map((targetGroup, index) => (
                      <TargetGroupItem
                        handleRemoveTargetGroup={handleRemoveTargetGroup}
                        index={index}
                        name={targetGroup.name}
                        uuid={targetGroup.uuid}
                        key={targetGroup.uuid}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardWrapper>
      </Container>
      <Backdrop isOpen={isDataFetching} />
    </>
  );
};

export default TargetGroupsList;
